import { NavLink, useNavigate } from 'react-router-dom';
import { useAgency } from '../lib/AgencyContext';
import { useAuth } from '../lib/AuthContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';

// "Više" na telefonu: ceo ekran kao u aplikaciji banke.
// Gore ime i naziv agencije, četiri brza dugmeta, promena naloga i godine, pa ceo meni.
// Donja traka ostaje vidljiva ispod ovog ekrana.

const initials = (name) =>
  String(name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const Chevron = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 6 6 6-6 6" />
  </svg>
);

export default function MoreMenu({ open, onClose, unpaid = 0 }) {
  const { activeAgency, profile, exitAgency, year, setYear, years, currentYear } = useAgency();
  const { signOut } = useAuth();
  const { t, tr } = useI18n();
  const navigate = useNavigate();

  if (!open) return null;

  const go = (to) => { onClose(); navigate(to); };
  const accountName = activeAgency?.name || profile?.company_name || t('yourAgencyDefault');

  const quick = [
    { key: 'profil', icon: 'user', label: tr('Profil', 'Profile', 'Профиль'), onClick: () => go('/profil') },
    { key: 'agencija', icon: 'building', label: tr('Agencija', 'Agency', 'Агентство'), onClick: () => go('/agencija') },
    { key: 'notif', icon: 'bell', label: tr('Notifikacije', 'Notifications', 'Уведомления'), onClick: () => go('/podesavanja/notifikacije') },
    { key: 'odjava', icon: 'logout', label: t('logout'), onClick: () => { onClose(); signOut(); }, dark: true },
  ];

  const sections = [
    {
      label: tr('Prodaja', 'Sales', 'Продажи'),
      items: [
        { to: '/fakturisanje', icon: 'receipt', label: t('invoicing'), count: unpaid },
        { to: '/fakturisanje?vrsta=profaktura', icon: 'fileText', label: tr('Profakture', 'Proformas', 'Проформы') },
        { to: '/avansi', icon: 'receipt', label: tr('Avansi', 'Advances', 'Авансы'), badge: tr('Uskoro', 'Soon', 'Скоро') },
        { to: '/efakture', icon: 'globe', label: tr('eFakture', 'e-Invoices', 'Э-счета') },
        { to: '/pazar', icon: 'chart', label: tr('Dnevni pazar', 'Daily takings', 'Дневная выручка') },
        { to: '/fiskalna-kasa', icon: 'calculator', label: t('navFiscalCash'), badge: tr('Uskoro', 'Soon', 'Скоро') },
        { to: '/klijenti', icon: 'users', label: t('clients') },
        { to: '/proizvodi', icon: 'package', label: t('navProducts') },
      ],
    },
    {
      label: tr('Porezi i evidencija', 'Taxes & records', 'Налоги и учёт'),
      items: [
        { to: '/porezi', icon: 'percent', label: t('taxes') },
        { to: '/kpo-knjiga', icon: 'book', label: t('navKpoBook') },
        { to: '/poreski-kalendar', icon: 'calendarDay', label: t('navTaxCalendar') },
        { to: '/kursna-lista', icon: 'exchange', label: t('navExchangeList') },
      ],
    },
    {
      label: tr('Ostalo', 'Other', 'Прочее'),
      items: [
        { to: '/preporuke', icon: 'gift', label: t('referrals') },
        { to: '/podrska', icon: 'headset', label: tr('Pomoć i podrška', 'Help & support', 'Помощь и поддержка') },
      ],
    },
  ];

  return (
    <div style={styles.screen} className="no-print more-menu" role="dialog" aria-modal="true"
      aria-label={tr('Više', 'More', 'Ещё')}>
      <div style={styles.inner}>
        {/* Fiksni deo gore (ne pomera se): četiri dugmeta */}
        <div style={styles.sticky}>
        {/* Četiri brza dugmeta */}
        <div style={styles.quickRow}>
          {quick.map((q) => (
            <button key={q.key} onClick={q.onClick} style={styles.quickBtn} className="more-quick">
              <span style={{ ...styles.quickIcon, ...(q.dark ? styles.quickIconDark : {}) }}>
                <Icon name={q.icon} size={19} />
              </span>
              <span style={styles.quickLabel}>{q.label}</span>
            </button>
          ))}
        </div>
        </div>

        {/* Promena naloga */}
        <button onClick={() => { onClose(); exitAgency(); }} style={styles.account} className="more-account">
          <span style={styles.accountAvatar}>{initials(accountName)[0]}</span>
          <span style={styles.accountText}>
            <span style={styles.accountName}>{accountName}</span>
            <span style={styles.accountSub}>{t('switchAccount')}</span>
          </span>
          <Icon name="chevronsUpDown" size={18} color="var(--ink-500)" />
        </button>

        {/* Godina */}
        <label style={{ ...styles.yearRow, ...(year !== currentYear ? styles.yearRowPast : {}) }}>
          <span style={styles.yearLabel}>
            <Icon name="calendar" size={16} />
            {t('yearLabel')}
          </span>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={styles.yearSelect}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        {year !== currentYear && (
          <button onClick={() => setYear(currentYear)} style={styles.backToCurrent}>
            {t('backToCurrentYear', currentYear)}
          </button>
        )}

        {/* Meni po grupama */}
        {sections.map((s) => (
          <div key={s.label}>
            <div style={styles.groupLabel} className="mono-label">{s.label}</div>
            {s.items.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={onClose} style={styles.row} className="more-row">
                <span style={styles.rowIcon}><Icon name={it.icon} size={17} /></span>
                <span style={styles.rowBody}>
                  <span style={styles.rowLabel} className="more-row-label">{it.label}</span>
                  {it.count > 0 && <span style={styles.countBadge}>{it.count}</span>}
                  {it.badge && <span style={styles.newBadge}>{it.badge}</span>}
                  <Chevron />
                </span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <style>{`
        @media (min-width: 1024px) { .more-menu { display: none !important; } }
        .more-row, .more-quick, .more-account { -webkit-tap-highlight-color: transparent; }
        .more-row:active { background: var(--paper); }
        .more-row:focus:not(:focus-visible), .more-quick:focus:not(:focus-visible),
        .more-account:focus:not(:focus-visible) { outline: none; }
        .more-row[aria-current="page"] .more-row-label { color: var(--lavender-700); }
      `}</style>
    </div>
  );
}

const styles = {
  screen: {
    position: 'fixed', inset: 0, zIndex: 24, background: 'var(--surface)', overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
  },
  inner: { maxWidth: 560, margin: '0 auto', padding: '16px 18px 8px' },

  head: { display: 'flex', alignItems: 'center', gap: 14 },
  avatar: {
    width: 48, height: 48, borderRadius: '50%', background: 'var(--paper)', color: 'var(--teal-800)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, flexShrink: 0,
  },
  headText: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  name: { fontSize: 16, fontWeight: 700, color: 'var(--ink-900)' },
  company: { fontSize: 11, fontWeight: 600, lineHeight: 1.3, color: 'var(--ink-500)', textTransform: 'uppercase' },

  sticky: {
    position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface)',
    margin: '-16px -18px 12px', padding: '16px 18px 0', borderBottom: '1px solid var(--line)',
  },
  quickRow: { display: 'flex', justifyContent: 'space-between', margin: '0 4px', paddingBottom: 14 },
  quickBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 68, border: 'none',
    background: 'transparent', padding: 0, fontFamily: 'inherit', cursor: 'pointer',
  },
  quickIcon: {
    width: 46, height: 46, borderRadius: 10, background: 'var(--lavender-100)', color: 'var(--lavender-700)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  quickIconDark: { background: 'var(--teal-800)', color: '#fff' },
  quickLabel: { fontSize: 12, fontWeight: 600, color: 'var(--ink-900)', textAlign: 'center' },

  account: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 52, padding: '0 12px',
    border: '1px solid var(--line)', borderRadius: 10, background: 'var(--paper)', textAlign: 'left',
    fontFamily: 'inherit', cursor: 'pointer',
  },
  accountAvatar: {
    width: 32, height: 32, borderRadius: 10, background: 'var(--teal-800)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0,
  },
  accountText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  accountName: { fontSize: 14, fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  accountSub: { fontSize: 12, color: 'var(--ink-500)' },

  yearRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 44,
    padding: '0 6px 0 12px', marginTop: 4, borderRadius: 8,
  },
  yearRowPast: { background: 'var(--gold-100)' },
  yearLabel: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--ink-500)' },
  yearSelect: {
    border: 'none', background: 'transparent', fontSize: 16, fontWeight: 800, color: 'var(--teal-800)',
    padding: '4px 2px', cursor: 'pointer',
  },
  backToCurrent: {
    border: 'none', background: 'none', color: 'var(--teal-700)', fontSize: 13, fontWeight: 600,
    padding: '0 14px 6px', textAlign: 'left', cursor: 'pointer',
  },

  groupLabel: {
    margin: '16px 0 0', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--ink-500)',
  },
  row: { display: 'flex', alignItems: 'center', gap: 12, minHeight: 50, color: 'var(--ink-900)', textDecoration: 'none' },
  rowIcon: {
    width: 34, height: 34, borderRadius: '50%', background: 'var(--paper)', color: 'var(--teal-800)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  rowBody: {
    flex: 1, alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 10,
    borderBottom: '1px solid var(--line)', minWidth: 0,
  },
  rowLabel: { flex: 1, fontSize: 15, fontWeight: 500, minWidth: 0 },
  countBadge: {
    fontSize: 12, fontWeight: 700, background: 'var(--teal-100)', color: 'var(--teal-800)',
    borderRadius: 999, padding: '2px 9px',
  },
  newBadge: {
    fontSize: 12, fontWeight: 700, background: 'var(--gold-100)', color: 'var(--gold-600)',
    borderRadius: 999, padding: '2px 9px',
  },
};
